import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { NewsItem } from '../../types';
import { OptimizedImage } from '../../components/common/OptimizedImage';

const NewsDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { newsId } = useParams<{ newsId: string }>();
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (newsId) {
      fetchNewsDetail();
    }
  }, [newsId, i18n.language]); // Refetch when language changes

  const fetchNewsDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      // Get current language code (e.g., 'en' or 'es')
      const language = i18n.language.split('-')[0]; // Extract base language (en, es) from 'en-US' or 'es-MX'
      const response = await api.get(`/news/${newsId}`, {
        params: {
          language: language // Send language preference to backend
        }
      });
      setNews(response.data.news || response.data);
    } catch (err: any) {
      console.error('Error loading news detail:', err);
      setError(err.message || t('feed.newsError'));
      toast.error(t('feed.newsError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-lg">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/feed')}
          className="mb-4 text-primary-600 hover:text-primary-700"
        >
          ← {t('common.back')}
        </button>
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-red-600">{error || t('feed.newsNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/feed')}
        className="mb-4 text-primary-600 hover:text-primary-700"
      >
        ← {t('common.back')}
      </button>

      <article className="bg-white rounded-lg shadow overflow-hidden">
        {/* Header Image */}
        {news.imageUrl && (
          <div className="relative w-full h-64 md:h-96">
            <OptimizedImage
              src={news.imageUrl}
              alt={news.title}
              className="w-full h-full"
              optimization={{ width: 1200, quality: 85 }}
              loading="eager"
              placeholder="blur"
              priority={true}
              objectPosition={news.imageFocalPointY != null ? `center ${news.imageFocalPointY * 100}%` : undefined}
            />
            <div className="absolute top-4 left-4 right-4 flex justify-between">
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                news.type === 'NEWS'
                  ? 'bg-blue-600 text-white'
                  : 'bg-green-600 text-white'
              }`}>
                {news.type === 'NEWS' ? t('feed.news') : t('feed.offers')}
              </span>
              {(news.store?.location || news.storeLocation) && (
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-black bg-opacity-60 text-white">
                  📍 {news.store?.location || news.storeLocation}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 md:p-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{news.title}</h1>
          <div className="flex items-center space-x-4 mb-6 text-sm text-gray-500">
            <span>{formatDate(news.createdAt)}</span>
            {news.store && (
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                {news.store.name}
              </span>
            )}
          </div>
          <div className="border-t border-gray-200 pt-6">
            <div className="prose max-w-none text-gray-700 whitespace-pre-line leading-relaxed">
              {news.content}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default NewsDetailPage;
